import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
} from '@angular/core';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { SharedComponentsModule } from '@wilson/shared-components';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { UserColorService } from '../../realtime/user-color.service';
import { getUserInitials } from '../../utils/get-user-initials';
import { User } from '@wilson/interfaces';
import { isNonAccessibleUser, NonAccessibleUser } from '../../utils/types';
import { IsNonAccessibleViewerPipe } from '../../utils/is-non-accessible-viewer.pipe';

@Component({
  selector: 'wilson-viewers-avatars',
  imports: [
    SharedComponentsModule,
    NzTooltipModule,
    TranslatePipe,
    IsNonAccessibleViewerPipe,
  ],
  templateUrl: './viewers-avatars.component.html',
  styleUrl: './viewers-avatars.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ViewersAvatarsComponent {
  private readonly userColorService = inject(UserColorService);
  private readonly translate = inject(TranslateService);

  readonly viewers = input<Array<User | NonAccessibleUser>>([]);
  readonly maxVisible = input(4);
  readonly tooltipTranslationKey = input(
    'page.shift_timeline.presence.viewers_tooltip',
  );

  protected readonly visibleViewers = computed(() =>
    this.viewers().slice(0, this.maxVisible()),
  );

  protected readonly overflowCount = computed(() =>
    Math.max(this.viewers().length - this.maxVisible(), 0),
  );

  protected readonly viewerNames = computed(() =>
    this.viewers()
      .map((viewer) => {
        if (isNonAccessibleUser(viewer)) {
          return this.translate.instant('general.unknown_user');
        } else {
          return `${viewer.firstName} ${viewer.lastName}`.trim();
        }
      })
      .join(', '),
  );

  protected getViewerInitials(viewer: User): string {
    return getUserInitials(viewer.firstName, viewer.lastName);
  }

  protected getViewerColor(viewer: User | NonAccessibleUser): string {
    return this.userColorService.getColorForUser(
      isNonAccessibleUser(viewer) ? undefined : (viewer.id as string),
    );
  }
}
